/** Game constants: weapon definitions and heal items. Part of src/constants.ts (re-exported there; import from '../constants'). */
import type { WeaponDef, WeaponKey, ItemKey } from '../types';

// =============================================================================
// WEAPONS
// =============================================================================

export const WEAPONS: Record<WeaponKey, WeaponDef> = {
  standard: {
    name: 'Standard',
    flavorName: 'Terrain Probe',
    icon: '●',
    blastRadius: 2.0,
    damage: 28,
    color: '#d4846a',
    cbpCost: 0,
    fuelDuration: 1.5,
  },
  heavy: {
    name: 'Heavy',
    flavorName: 'Deep Core Sample',
    icon: '◉',
    blastRadius: 3.2,
    damage: 48,
    color: '#c4a060',
    cbpCost: 5,
    fuelDuration: 2.0,
  },
  cluster: {
    name: 'Cluster',
    flavorName: 'Multi-Point Survey Array',
    icon: '⦿',
    blastRadius: 1.3,
    damage: 14,
    color: '#b090c4',
    cbpCost: 10,
    fuelDuration: 1.0,
    cluster: {
      count: 5,
      spread: 0.5,
      subWeapon: 'standard',
    },
  },
  nuke: {
    name: 'Nuke',
    flavorName: 'Comprehensive Geological Assessment',
    icon: '☢',
    blastRadius: 5.5,
    damage: 75,
    color: '#a0c4a0',
    cbpCost: 20,
    fuelDuration: 3.0,
  },
  probe: {
    name: 'Probe',
    flavorName: 'Ecological Recon Drone',
    icon: '◎',
    blastRadius: 0,
    damage: 0,
    color: '#a0b8d4',
    cbpCost: 1,
    fuelDuration: 8.0,
    reveals: true,
  },
  punch: {
    name: 'Railgun',
    flavorName: 'Direct Assessment Tool',
    icon: '▸',
    blastRadius: 0,
    damage: 40,
    color: '#d4a0a0',
    cbpCost: 1,
    fuelDuration: 0,
    noCrater: true,
    hitscan: true,
  },
  antigrav: {
    name: 'Antigrav',
    flavorName: 'Gravitational Displacement Device',
    icon: '◈',
    blastRadius: 4.0,
    damage: 0,
    color: '#6040cc',
    cbpCost: 8,            // owner (Oct 9): "more expensive" — a launch for the price of two heavies
    fuelDuration: 1.5,
    noCrater: true,
    knockbackForce: 3.0,
    special: 'antigrav',
  },
  // §12.7 field class (S4 experiment): the same-planet launcher. A power cap below escape speed keeps it on the
  // planet; the elevation floor makes every shot a lob; low knockback so it kills by blast, not by launch.
  mortar: {
    name: 'Mortar',
    flavorName: 'Field Displacement Tube',
    icon: '⌒',
    blastRadius: 2.4,
    damage: 34,
    color: '#c08040',
    cbpCost: 3,
    fuelDuration: 0,
    knockbackForce: 0.35,
    rangeClass: 'field',
    maxPower: 42,
    minElevation: 0.85,
  },
  gravBomb: {
    name: 'Gravity Bomb',
    flavorName: 'Localized Mass Anomaly',
    icon: '⊛',
    blastRadius: 3.5,
    damage: 5,
    color: '#4020aa',
    cbpCost: 4,
    fuelDuration: 1.5,
    noCrater: true,
    knockbackForce: 2.5,
    special: 'gravBomb',
  },
  tether: {
    name: 'Tether Mine',
    flavorName: 'Kinetic Restraint Payload',
    icon: '⊗',
    blastRadius: 3.0,
    damage: 0,
    color: '#cc4040',
    cbpCost: 3,
    fuelDuration: 1.0,
    noCrater: true,
    special: 'tether',
  },
  terraformer: {
    name: 'Terraformer',
    flavorName: 'Geological Restoration Charge',
    icon: '▲',
    blastRadius: 2.5,
    damage: 0,
    color: '#60aa30',
    cbpCost: 3,
    fuelDuration: 1.5,
    special: 'terraformer',
  },
  emp: {
    name: 'EMP Pulse',
    flavorName: 'Electromagnetic Survey Disruptor',
    icon: '⚡',
    blastRadius: 3.5,
    damage: 8,
    color: '#30aaff',
    cbpCost: 3,
    fuelDuration: 1.0,
    special: 'emp',
    empDuration: 2,
  },
  drill: {
    name: 'Drill Missile',
    flavorName: 'Sub-Surface Penetrator',
    icon: '⊙',
    blastRadius: 2.0,
    damage: 35,
    color: '#c8a030',
    cbpCost: 5,
    fuelDuration: 1.0,
    special: 'drill',
  },
  fire: {
    name: 'Fire Missile',
    flavorName: 'Incendiary Remediation Device',
    icon: '⬡',
    blastRadius: 2.5,
    damage: 20,
    color: '#ff6020',
    cbpCost: 3,
    fuelDuration: 1.5,
    special: 'fire',
    fireDuration: 2,
    fireDamagePerTurn: 8,
  },
};

export const WEAPON_KEYS: WeaponKey[] = Object.keys(WEAPONS) as WeaponKey[];

// =============================================================================
// HEAL ITEMS
// =============================================================================

export interface HealItemDef {
  name: string;
  flavorName: string;
  cbpCost: number;
  healPercent: number;
}

export const HEAL_ITEMS: Record<ItemKey, HealItemDef> = {
  healSmall: { name: 'Small Healthpack', flavorName: 'Field Repair Kit', cbpCost: 2, healPercent: 30 },
  healLarge: { name: 'Large Healthpack', flavorName: 'Emergency Med Station', cbpCost: 5, healPercent: 100 },
};

export const HEAL_MOVEMENT_COST = 2;

// =============================================================================
// THE ANTI-DROPSHIP MISSILE (65.49 step 1: the homing core's dials, core/homing.ts)
// =============================================================================

/**
 * 65.49 (the owner: "make it feel fun in the game system, such that it's not a guaranteed hit, but there's some coherence to
 * what's a likely hit and what's an unlikely hit"): starting dials for feel at the game's scale (a dropship cruises 40 people
 * up at 40 people a second), for the owner to tune by playing. People, seconds, radians.
 * - `lock`: a ship inside `cone` (the half angle about the aim) for `hold` seconds is locked; none past `reach`.
 * - `homing`: out of the tube at `launchSpeed`, a `coast` clear of the gunner (no steering), then the motor: up to
 *   `topSpeed` at `boost` a second², turning its heading at most `turn` a second toward where the target will be, `leadGain`
 *   of the way (0: its current position, 1: a full lead; 0.25 from scripts/homingOdds.ts's table, sweeps/2026-10-21-65.49-lead-gain.md); it hits when it passes
 *   within `fuse` of the target's body, is spent after `life`, and loses a target out of its sight longer than `lostAfter`
 *   (it flies straight on).
 * - `reload` seconds after a shot; `reserve` missiles carried.
 * - `damage`: its burst's at the heart (step 2: the rocket's, 90, over the rocket's circle; a ship takes it by its parts as
 *   from a rocket's burst, core/shipDamage.ts shipBlastPass), for the owner to tune.
 */
export const ANTI_DROPSHIP_MISSILE_DIALS = {
  lock: { cone: (6 * Math.PI) / 180, hold: 1.5, reach: 300 },
  homing: { launchSpeed: 13.3, coast: 0.3, boost: 144, topSpeed: 120, turn: Math.PI / 4, fuse: 2, life: 4, leadGain: 0.25, lostAfter: 0.5 },
  reload: 6,
  reserve: 1,
  damage: 90,
} as const;
/**
 * 69.50 step 2a (DECISIONS 263, the owner: "we'll probably have a few classes of missile, maybe 3 small medium large, adm is
 * small, not sure if this should be medium or large"): the missile classes, each a seeker's `lock`, a flight's `homing` and a
 * burst's `damage` in ANTI_DROPSHIP_MISSILE_DIALS' shape. **Small** is the anti-dropship missile as built (a person's tube,
 * the missile technical's rack); **medium** the air-defence site's (AIR_DEFENCE_SITE.missileClass), a launcher's round that
 * reaches and hits harder and turns less; **large** kept for a later strategic class. Medium's and large's dials are starting
 * values, not tuned (a ship's hull is 400, core/shipBody.ts SHIP_BODY_HEALTH: small's 90 a burst, medium's 180, large's 400).
 */
export const MISSILE_CLASSES = {
  small: { lock: ANTI_DROPSHIP_MISSILE_DIALS.lock, homing: ANTI_DROPSHIP_MISSILE_DIALS.homing, damage: ANTI_DROPSHIP_MISSILE_DIALS.damage },
  medium: {
    lock: { cone: (10 * Math.PI) / 180, hold: 2, reach: 500 },
    homing: { launchSpeed: 20, coast: 0.4, boost: 160, topSpeed: 150, turn: Math.PI / 2, fuse: 3, life: 6, leadGain: 0.25, lostAfter: 1 },   // (a ~95-person turn at its top speed: a world is ~1,100 people round, its ships ~200–500 off — π/5 at 200 flew 320-person circles into the ground)
    damage: 180,
  },
  large: {
    lock: { cone: (10 * Math.PI) / 180, hold: 3, reach: 1500 },
    homing: { launchSpeed: 25, coast: 0.8, boost: 180, topSpeed: 250, turn: Math.PI / 3, fuse: 4, life: 10, leadGain: 0.25, lostAfter: 1 },
    damage: 400,
  },
} as const;
export type MissileClass = keyof typeof MISSILE_CLASSES;
